import { Box, Stack, Typography } from '@mui/material'
import { FeatureFlagIdStepUpAuth, FeatureFlagIdRequireDistinctApproversUi } from 'c1gen/features'
import { C1Tooltip } from 'c1ui/components/C1Tooltip'
import { DynamicEditor } from 'components/core/editor/editor'
import { C1Icon } from 'components/core/icons/C1Icon'
import { DisplayUserNamesWithFetch } from 'components/core/search/users'
import { roundedDurationString } from 'components/core/time/duration/duration'
import { C1WebhookDisplayWithFetch } from 'components/core/webhooks/WebhookDisplayName'
import { HasFeature } from 'components/no-ui/capability'
import { C1EntitlementDisplayNameWithFetch } from 'components/pages/entitlement/display-name'
import { ApprovalOneOfKind, ProvisionOneOfKind, StepOneOfKind } from 'components/pages/policy/type'
import { UserDisplayNameWithFetch } from 'components/pages/user/user-display-name'
import { Check, X } from 'lucide-react'
import { PolicyStep, PolicyType, Provision } from 'pbts/c1/api/policy/v1/policy'
import { Approval, AppEntitlementReference } from 'pbts/c1/api/policy/v1/policy_steps_approval'
import { Wait, WaitDuration, WaitUntilTime } from 'pbts/c1/api/policy/v1/policy_steps_wait'
import { Duration } from 'pbts/google/protobuf/duration'
import { FC } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'

import { StepUpProviderTooltipContent } from './StepUpProviderTooltipContent'

export const PolicyStepTypeMapping = {
    accept: {
        [PolicyType.ACCESS_REQUEST]: 'Auto-approve',
        [PolicyType.CERTIFY]: 'Auto-certify',
        [PolicyType.GRANT]: 'Auto-approve',
        [PolicyType.PROVISION]: 'Auto-provision',
        [PolicyType.REVOKE]: 'Auto-confirm',
        [PolicyType.UNSPECIFIED]: '(unspecified)',
    },
    reject: {
        [PolicyType.ACCESS_REQUEST]: 'Auto-deny',
        [PolicyType.CERTIFY]: 'Auto-remove',
        [PolicyType.GRANT]: 'Auto-deny',
        [PolicyType.PROVISION]: 'Auto-deny',
        [PolicyType.REVOKE]: 'Auto-decline',
        [PolicyType.UNSPECIFIED]: '(unspecified)',
    },
}

export const getSelfApprovalEnabled = (approval: Approval): boolean => {
    switch (approval?.typ?.oneofKind) {
        case ApprovalOneOfKind.USERS:
            return approval.typ.users.allowSelfApproval
        case ApprovalOneOfKind.MANAGER:
            return approval.typ.manager.allowSelfApproval
        case ApprovalOneOfKind.ENTITLEMENT_OWNERS:
            return approval.typ.entitlementOwners.allowSelfApproval
        case ApprovalOneOfKind.APP_OWNERS:
            return approval.typ.appOwners.allowSelfApproval
        case ApprovalOneOfKind.GROUP:
            return approval.typ.group.allowSelfApproval
        case ApprovalOneOfKind.EXPRESSION:
            return approval.typ.expression.allowSelfApproval
        case ApprovalOneOfKind.SELF:
            return true
        case ApprovalOneOfKind.WEBHOOK:
            return false
        case ApprovalOneOfKind.AGENT:
            return false
        case ApprovalOneOfKind.RESOURCE_OWNERS:
            return approval.typ.resourceOwners.allowSelfApproval
        default:
            return false
    }
}

export const getSelfProvisionEnabled = (provision?: Provision): boolean => {
    switch (provision?.provisionPolicy?.typ?.oneofKind) {
        case ProvisionOneOfKind.CONNECTOR:
            return false
        default:
            return true
    }
}

const policyStepTypeString = (stepKind: string, policyType: PolicyType, intl: IntlShape): string | null => {
    const map = {
        accept: {
            [PolicyType.CERTIFY]: intl.formatMessage({
                id: 'component_policy_step_certify_accept',
                defaultMessage: 'Always certify the access',
            }),
            [PolicyType.GRANT]: intl.formatMessage({
                id: 'component_policy_step_grant_accept',
                defaultMessage: 'Always approve the request',
            }),
            [PolicyType.REVOKE]: intl.formatMessage({
                id: 'component_policy_step_revoke_accept',
                defaultMessage: 'Always confirm that the access should be revoked',
            }),
            [PolicyType.PROVISION]: intl.formatMessage({
                id: 'component_policy_step_provision_accept',
                defaultMessage: 'Always provision',
            }),
            // This policy type is deprecated. It's only included to eliminate the chance of this component throwing a JS error
            [PolicyType.ACCESS_REQUEST]: intl.formatMessage({
                id: 'component_policy_step_access_accept',
                defaultMessage: 'Always approve',
            }),
            [PolicyType.UNSPECIFIED]: intl.formatMessage({
                id: 'component_policy_step_unspecified_accept',
                defaultMessage: '(unspecified)',
            }),
        },
        reject: {
            [PolicyType.CERTIFY]: intl.formatMessage({
                id: 'component_policy_step_certify_reject',
                defaultMessage: 'Always mark the access for removal',
            }),
            [PolicyType.GRANT]: intl.formatMessage({
                id: 'component_policy_step_grant_reject',
                defaultMessage: 'Always deny the request',
            }),
            [PolicyType.REVOKE]: intl.formatMessage({
                id: 'component_policy_step_revoke_reject',
                defaultMessage: 'Always decline the proposed revocation',
            }),
            [PolicyType.PROVISION]: intl.formatMessage({
                id: 'component_policy_step_provision_reject',
                defaultMessage: 'Always reject',
            }),
            // This policy type is deprecated. It's only included to eliminate the chance of this component throwing a JS error
            [PolicyType.ACCESS_REQUEST]: intl.formatMessage({
                id: 'component_policy_step_access_reject',
                defaultMessage: 'Always deny',
            }),
            [PolicyType.UNSPECIFIED]: intl.formatMessage({
                id: 'component_policy_step_unspecified_reject',
                defaultMessage: '(unspecified)',
            }),
        },
    }

    if (!stepKind && stepKind !== StepOneOfKind.ACCEPT && stepKind !== StepOneOfKind.REJECT) {
        return null
    }

    return map[stepKind][policyType]
}

type StepProps = {
    step: PolicyStep
    policyType: PolicyType
    index: number
}

export const Step: FC<StepProps> = ({ step, index, policyType }) => {
    const stepKind = step.step.oneofKind
    switch (stepKind) {
        case StepOneOfKind.APPROVAL:
            return <ApprovalStep step={step} index={index} policyType={policyType} />
        case StepOneOfKind.WAIT:
            return <WaitStep step={step} index={index} />
        case StepOneOfKind.ACCEPT:
        case StepOneOfKind.REJECT:
            return <AcceptRejectStep step={step} policyType={policyType} />
        default:
            return <FormattedMessage defaultMessage="Unknown step type" id="component_policy_step_unknown" />
    }
}

type AcceptRejectStepProps = {
    step: PolicyStep,
    policyType: PolicyType
}

const AcceptRejectStep: FC<AcceptRejectStepProps> = ({ step, policyType }) => {
    const stepKind = step.step.oneofKind
    const intl = useIntl()

    if (!stepKind && stepKind !== StepOneOfKind.ACCEPT && stepKind !== StepOneOfKind.REJECT) {
        return null
    }

    return <Stack direction="row" spacing={2} alignItems="center">
        {stepKind === StepOneOfKind.ACCEPT ? <C1Icon icon={Check} sx={{ color: 'success.main' }} /> : <C1Icon icon={X} sx={{ color: 'error.main' }} />}
        <Typography variant="h6" component="span">
            {policyStepTypeString(stepKind, policyType, intl)}
            {stepKind === StepOneOfKind.ACCEPT && step.step.accept.acceptMessage && (
                <FormattedMessage
                    defaultMessage=' and post: <c>"{comment}"</c>'
                    values={{
                        c: (chunks) => <Typography component="span">{chunks}</Typography>,
                        comment: step.step.accept.acceptMessage,
                    }}
                    id="component_policy_step_accept_comment"
                />
            )}
            {stepKind === StepOneOfKind.REJECT && step.step.reject.rejectMessage && (
                <FormattedMessage
                    defaultMessage=' and post: <c>"{comment}"</c>'
                    values={{
                        c: (chunks) => <Typography component="span">{chunks}</Typography>,
                        comment: step.step.reject.rejectMessage,
                    }}
                    id="component_policy_step_reject_comment"
                />
            )}
        </Typography>
    </Stack>
}

type ApprovalStepProps = {
    step: PolicyStep
    policyType: PolicyType
    index: number
}

const ApprovalStep: FC<ApprovalStepProps> = ({ step, index }) => {
    const instance: Approval = step.step[step.step.oneofKind ?? StepOneOfKind.APPROVAL]
    const approval = instance.typ[instance.typ.oneofKind ?? '']

    let approvalElement
    let fallbackUserIDs: string[] = []
    let fallbackGroupIDs: AppEntitlementReference[] = []
    let isGroupFallbackEnabled = false

    switch (instance.typ.oneofKind) {
        case ApprovalOneOfKind.USERS:
            approvalElement = <DisplayUserNamesWithFetch margin={false} userIds={instance.typ.users.userIds} />
            break
        case ApprovalOneOfKind.MANAGER:
            fallbackUserIDs = instance.typ.manager.fallbackUserIds
            fallbackGroupIDs = instance.typ.manager.fallbackGroupIds
            isGroupFallbackEnabled = instance.typ.manager.isGroupFallbackEnabled
            approvalElement = <Typography>
                <FormattedMessage defaultMessage="Manager" id="component_policy_step_manager" />
            </Typography>
            break
        case ApprovalOneOfKind.APP_OWNERS:
            approvalElement = <Typography>
                <FormattedMessage defaultMessage="App owner" id="component_policy_step_app_owner" />
            </Typography>
            break
        case ApprovalOneOfKind.GROUP:
            fallbackUserIDs = instance.typ.group.fallbackUserIds
            fallbackGroupIDs = instance.typ.group.fallbackGroupIds
            isGroupFallbackEnabled = instance.typ.group.isGroupFallbackEnabled

            approvalElement = <C1EntitlementDisplayNameWithFetch
                appId={instance.typ.group.appId}
                entitlementId={instance.typ.group.appGroupId}
            />
            break
        case ApprovalOneOfKind.SELF:
            fallbackUserIDs = instance.typ.self.fallbackUserIds
            fallbackGroupIDs = instance.typ.self.fallbackGroupIds
            isGroupFallbackEnabled = instance.typ.self.isGroupFallbackEnabled
            approvalElement = <Typography>
                <FormattedMessage defaultMessage="Account owner" id="component_policy_step_self" />
            </Typography>
            break
        case ApprovalOneOfKind.ENTITLEMENT_OWNERS:
            fallbackUserIDs = instance.typ.entitlementOwners.fallbackUserIds
            fallbackGroupIDs = instance.typ.entitlementOwners.fallbackGroupIds
            isGroupFallbackEnabled = instance.typ.entitlementOwners.isGroupFallbackEnabled
            approvalElement = <Typography>
                <FormattedMessage defaultMessage="Entitlement owner" id="component_policy_step_entitlement_owner" />
            </Typography>
            break
        case ApprovalOneOfKind.RESOURCE_OWNERS:
            fallbackUserIDs = instance.typ.resourceOwners.fallbackUserIds
            fallbackGroupIDs = instance.typ.resourceOwners.fallbackGroupIds
            isGroupFallbackEnabled = instance.typ.resourceOwners.isGroupFallbackEnabled
            approvalElement = <Typography>
                <FormattedMessage defaultMessage="Resource owner" id="component_policy_step_resource_owner" />
            </Typography>
            break
        case ApprovalOneOfKind.EXPRESSION:
            fallbackUserIDs = instance.typ.expression.fallbackUserIds
            fallbackGroupIDs = instance.typ.expression.fallbackGroupIds
            isGroupFallbackEnabled = instance.typ.expression.isGroupFallbackEnabled
            approvalElement = <DynamicEditor
                value={instance.typ.expression.expressions[0] || ''}
                defaultLanguage="cel"
                options={{ readOnly: true, domReadOnly: true, lineNumbers: 'off' }}
            />
            break
        case ApprovalOneOfKind.WEBHOOK:
            approvalElement = <C1WebhookDisplayWithFetch id={instance.typ.webhook.webhookId} />
            break
        case ApprovalOneOfKind.AGENT:
            approvalElement = <UserDisplayNameWithFetch id={instance.typ.agent.agentUserId} includeDetails={false} />
            break
        default:
            fallbackUserIDs = []
            fallbackGroupIDs = []
            isGroupFallbackEnabled = false

            approvalElement = <Typography>
                <FormattedMessage defaultMessage="Unknown approval type" id="component_policy_step_unknown_approval" />
            </Typography>
    }

    return (
        <Box sx={{ '&:not(:last-child)': { mb: 3, pb: 3, borderBottom: '1px solid', borderBottomColor: 'border.primary' } }}>
            <Stack direction="row" spacing={4}>
                <Stack flexGrow={1} spacing={2}>
                    <Typography variant="eyebrow" component="span">
                        <FormattedMessage defaultMessage="Step {count}" id="component_policy_step" values={{ count: index + 1 }} />
                    </Typography>
                    <Box>
                        <Typography variant="h6" component="div" mb={1}>
                            {instance.typ.oneofKind !== ApprovalOneOfKind.WEBHOOK ? (
                                <FormattedMessage defaultMessage="Assign this task to:" id="component_policy_step_assign_review_to" />
                            ) : (
                                <FormattedMessage defaultMessage="Execute webhook:" id="component_policy_step_execute_webhook" />
                            )}
                        </Typography>
                        {approvalElement}
                    </Box>
                    {/* Fallback users section */}
                    {!isGroupFallbackEnabled && fallbackUserIDs?.length > 0 && (
                        <Box>
                            <Typography variant="h6" component="div" mb={1}>
                                <FormattedMessage
                                    defaultMessage="If unable to identify the primary reviewer, fallback to:"
                                    id="component_policy_step_fallback_text"
                                />
                            </Typography>
                            <DisplayUserNamesWithFetch margin={false} userIds={fallbackUserIDs} />
                        </Box>
                    )}

                    {/* Fallback groups section */}
                    {isGroupFallbackEnabled && fallbackGroupIDs?.length > 0 && (
                        <Box>
                            <Typography variant="h6" component="div" mb={1}>
                                <FormattedMessage
                                    defaultMessage="If unable to identify the primary reviewer, fallback to:"
                                    id="component_policy_step_fallback_text"
                                />
                            </Typography>
                            {fallbackGroupIDs.map((group, i) => (
                                <C1EntitlementDisplayNameWithFetch
                                    key={i}
                                    appId={group.appId}
                                    entitlementId={group.appEntitlementId}
                                />
                            ))}
                        </Box>
                    )}
                </Stack>

                {instance.typ.oneofKind !== ApprovalOneOfKind.WEBHOOK && instance.typ.oneofKind !== ApprovalOneOfKind.AGENT && (
                    <Stack spacing={1} sx={{ minWidth: { md: '280px' } }}>
                        <StepOption
                            conditions={[
                                [
                                    instance.allowReassignment && instance.requireReassignmentReason,
                                    <FormattedMessage defaultMessage="Reassignment allowed with justification" id="component_policy_step_reassignment_allowed_with_reason" />,
                                ],
                                [
                                    instance.allowReassignment,
                                    <FormattedMessage defaultMessage="Reassignment allowed" id="component_policy_step_reassignment_allowed" />,
                                ],
                            ]}
                            fallback={<FormattedMessage defaultMessage="No reassignment" id="component_policy_step_no_reassignment" />}
                        />
                        <StepOption
                            conditions={[
                                [
                                    instance.allowDelegation,
                                    <FormattedMessage defaultMessage="Task delegation enabled" id="component_policy_step_allow_delegation" />,
                                ],
                            ]}
                            fallback={<FormattedMessage defaultMessage="Task delegation disabled" id="component_policy_step_no_allow_delegation" />}
                        />
                        <StepOption
                            conditions={[
                                [approval?.allowSelfApproval || instance.typ.oneofKind === 'self', <FormattedMessage defaultMessage="Self-review allowed" id="component_policy_step_allow_self_review" />],
                            ]}
                            fallback={<FormattedMessage defaultMessage="No self-review" id="component_policy_step_no_self_review" />}
                        />
                        <HasFeature feature={FeatureFlagIdRequireDistinctApproversUi}>
                            <StepOption
                                conditions={[
                                    [
                                        (instance.typ.oneofKind === 'users' && instance.typ.users.requireDistinctApprovers)
                                        || (instance.typ.oneofKind === 'manager' && instance.typ.manager.requireDistinctApprovers)
                                        || (instance.typ.oneofKind === 'entitlementOwners' && instance.typ.entitlementOwners.requireDistinctApprovers)
                                        || (instance.typ.oneofKind === 'appOwners' && instance.typ.appOwners.requireDistinctApprovers)
                                        || (instance.typ.oneofKind === 'group' && instance.typ.group.requireDistinctApprovers)
                                        || (instance.typ.oneofKind === 'expression' && instance.typ.expression.requireDistinctApprovers)
                                        || (instance.typ.oneofKind === 'resourceOwners' && instance.typ.resourceOwners.requireDistinctApprovers),
                                        <FormattedMessage defaultMessage="Distinct approvers required" id="component_policy_step_require_distinct_approvers" />,
                                    ],
                                ]}
                                fallback={<FormattedMessage defaultMessage="Require distinct approvers" id="component_policy_step_no_require_distinct_approvers" />}
                            />
                        </HasFeature>
                        <StepOption
                            conditions={[
                                [instance.requireApprovalReason && instance.requireDenialReason, <FormattedMessage defaultMessage="Justification required" id="component_policy_step_require_justification" />],
                                [instance.requireApprovalReason, <FormattedMessage defaultMessage="Justification required for approval" id="component_policy_step_require_justification_for_approval" />],
                                [instance.requireDenialReason, <FormattedMessage defaultMessage="Justification required for denial" id="component_policy_step_require_justification_for_denial" />],
                            ]}
                            fallback={<FormattedMessage defaultMessage="Justification not required" id="component_policy_step_no_justification" />}
                        />
                        <HasFeature feature={FeatureFlagIdStepUpAuth}>
                            <C1Tooltip
                                PopperProps={{
                                    sx: {
                                        '& .MuiTooltip-tooltip': { maxWidth: 'none' },
                                    },
                                }}
                                title={<StepUpProviderTooltipContent providerId={instance.requiresStepUpProviderId} />}
                                disableHoverListener={!instance.requiresStepUpProviderId}
                            >
                                <Box>
                                    <StepOption
                                        conditions={[
                                            [!!instance.requiresStepUpProviderId, <FormattedMessage defaultMessage="Step-up authentication required" id="component_policy_step_require_step_up" />],
                                        ]}
                                        fallback={<FormattedMessage defaultMessage="No step-up authentication required" id="component_policy_step_no_step_up" />}
                                    />
                                </Box>
                            </C1Tooltip>
                        </HasFeature>
                    </Stack>
                )}
                {
                    instance.typ.oneofKind === ApprovalOneOfKind.AGENT && instance.typ.agent.instructions && <Stack direction="row" spacing={4}>
                        <Stack spacing={1} pr={2}>
                            <Typography variant="h6" component="div">
                                <FormattedMessage defaultMessage="Step specific instructions" id="component_policy_step_step_specific_instructions" />
                            </Typography>
                            <Typography sx={{ maxWidth: '32em' }}>
                                {' '}
                                {instance.typ.agent.instructions}
                                {' '}
                            </Typography>
                        </Stack>
                    </Stack>
                }
            </Stack>
        </Box>
    )
}

type StepOptionProps = {
    conditions: [boolean, React.ReactElement][]
    fallback: React.ReactElement
}

const StepOption: FC<StepOptionProps> = ({ conditions, fallback }) => {
    const activeCondition = conditions.find(([condition]) => condition)

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            {activeCondition ? <C1Icon icon={Check} sx={{ color: 'success.main' }} /> : <C1Icon icon={X} sx={{ color: 'error.main' }} />}
            <Typography>
                {activeCondition ? activeCondition[1] : fallback}
            </Typography>
        </Stack>
    )
}

type WaitStepConditionProps = {
    instance: Wait
    condition: string
}

const WaitStepCondition: FC<WaitStepConditionProps> = ({ instance, condition }) => {
    return <Box>
        <Typography variant="h6" component="div" mb={1}>
            <FormattedMessage
                defaultMessage="Wait for: <span>{name}</span>"
                id="component_policy_step_wait_for_condition"
                values={{
                    name: instance.name,
                    span: (chunks) => <Typography component="span">{chunks}</Typography>,
                }}
            />
        </Typography>
        <DynamicEditor
            value={condition || ''}
            defaultLanguage="cel"
            options={{ readOnly: true, domReadOnly: true, lineNumbers: 'off' }}
        />
    </Box>
}

type WaitStepDurationProps = {
    instance: Wait
    duration: WaitDuration
}

const WaitStepDuration: FC<WaitStepDurationProps> = ({ duration }) => {
    const intl = useIntl()
    return <Box>
        <Typography variant="h6" component="div">
            <FormattedMessage
                defaultMessage="Wait for <span>{name}</span>"
                id="component_policy_step_wait_for_duration"
                values={{
                    name: roundedDurationString(intl, duration.duration),
                    span: (chunks) => <Typography component="span">{chunks}</Typography>,
                }}
            />
        </Typography>
    </Box>
}

type WaitStepUntilTimeProps = {
    instance: Wait
    until: WaitUntilTime
}

const WaitStepUntilTime: FC<WaitStepUntilTimeProps> = ({ until }) => {
    const formattedHours = until.hours.toString().padStart(2, '0')
    const formattedMinutes = until.minutes.toString().padStart(2, '0')

    return (
        <Box>
            <Typography variant="h6" component="div">
                <FormattedMessage
                    defaultMessage="Wait until <span>{hours}:{minutes} {timezone}</span>"
                    id="component_policy_step_wait_for_until_time"
                    values={{
                        hours: formattedHours,
                        minutes: formattedMinutes,
                        timezone: until.timezone,
                        span: (chunks) => <Typography component="span">{chunks}</Typography>,
                    }}
                />
            </Typography>
        </Box>
    )
}

type WaitStepProps = {
    step: PolicyStep,
    index: number
}

const WaitStep: FC<WaitStepProps> = ({ step, index }) => {
    const stepKind = step.step.oneofKind
    const intl = useIntl()

    if (!stepKind || stepKind !== StepOneOfKind.WAIT) return null

    const instance: Wait = step.step[step.step.oneofKind ?? StepOneOfKind.WAIT]

    let untilDetails: React.ReactNode
    switch (instance.until.oneofKind) {
        case 'condition':
            untilDetails = <WaitStepCondition instance={instance} condition={instance.until.condition.condition} />
            break
        case 'duration':
            untilDetails = <WaitStepDuration instance={instance} duration={instance.until.duration} />
            break
        case 'untilTime':
            untilDetails = <WaitStepUntilTime instance={instance} until={instance.until.untilTime} />
            break
        default:
            return null
    }

    return (
        <Box sx={{ '&:not(:last-child)': { mb: 3, pb: 3, borderBottom: '1px solid', borderBottomColor: 'border.primary' } }}>
            <Stack direction="row" spacing={4}>
                <Stack flexGrow={1} spacing={2}>
                    <Typography variant="eyebrow" component="span">
                        <FormattedMessage defaultMessage="Step {count}" id="component_policy_step" values={{ count: index + 1 }} />
                    </Typography>
                    {untilDetails}
                    <Stack spacing={1}>
                        {instance.commentOnFirstWait && <Typography variant="h6" component="div">
                            <FormattedMessage
                                defaultMessage="Post this comment while waiting: <span>{comment}</span>"
                                id="component_policy_step_post_comment"
                                values={{
                                    comment: instance.commentOnFirstWait,
                                    span: (chunks) => <Typography component="span">{chunks}</Typography>,
                                }}
                            />
                        </Typography>}
                        {!(instance.timeoutDuration?.nanos === 0 && instance.timeoutDuration?.seconds === 0) && (
                            <Typography variant="h6" component="div">
                                <FormattedMessage
                                    id="component_policy_step_post_comment_on_timeout"
                                    defaultMessage="Timeout after {amount}{hasComment, select, true { and post: <span>{comment}</span>} other {}}"
                                    values={{
                                        amount:
                                            roundedDurationString(intl, Duration.create({ seconds: instance.timeoutDuration?.seconds })),
                                        comment: instance.commentOnTimeout,
                                        hasComment: !!instance.commentOnTimeout,
                                        span: (chunks) => <Typography component="span">{chunks}</Typography>,
                                    }}
                                />
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Box>
    )
}
